import React, { useState, useEffect } from 'react';
import { Star, Plus, Edit, Trash2, X, Save, Loader2, Eye, EyeOff } from 'lucide-react';
import { getAdminTestimonials, updateTestimonial, createTestimonial, deleteTestimonial } from '../../utils/adminApi';
import { useAdmin } from '../../contexts/AdminContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const TestimonialsManagement = () => {
  const { token } = useAdmin();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    text: { en: '', es: '', de: '' },
    rating: 5,
    active: true
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const data = await getAdminTestimonials();
      setTestimonials(data.testimonials || []);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      text: { en: '', es: '', de: '' },
      rating: 5,
      active: true
    });
    setEditingTestimonial(null);
  };

  const openEditModal = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name || '',
      company: testimonial.company || '',
      text: testimonial.text || { en: '', es: '', de: '' },
      rating: testimonial.rating || 5,
      active: testimonial.active !== false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.text.en) {
      toast.error('Name and testimonial text (English) are required');
      return;
    }

    setSaving(true);
    try {
      if (editingTestimonial) {
        await updateTestimonial(token, editingTestimonial.testimonial_id, formData);
        toast.success('Testimonial updated successfully');
      } else {
        await createTestimonial(token, formData);
        toast.success('Testimonial created successfully');
      }
      setShowModal(false);
      resetForm();
      loadTestimonials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (testimonialId, name) => {
    if (!window.confirm(`Are you sure you want to delete testimonial from "${name}"?`)) return;
    
    try {
      await deleteTestimonial(token, testimonialId);
      toast.success('Testimonial deleted');
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleToggleActive = async (testimonial) => {
    try {
      await updateTestimonial(token, testimonial.testimonial_id, { active: !testimonial.active });
      toast.success(`Testimonial ${testimonial.active ? 'hidden' : 'visible'}`);
      loadTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#3a3d42]'}`} />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="testimonials-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Star className="w-7 h-7 text-[#D4AF37]" />
            Testimonials Management
          </h1>
          <p className="text-[#9CA3AF] mt-1">Manage client testimonials and reviews</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Testimonial
        </Button>
      </div>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((testimonial) => (
          <div
            key={testimonial.testimonial_id}
            className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-1">
                {renderStars(testimonial.rating)}
              </div>
              <span className={`px-2 py-1 rounded text-xs ${
                testimonial.active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {testimonial.active !== false ? 'Visible' : 'Hidden'}
              </span>
            </div>
            
            <p className="text-[#9CA3AF] text-sm mb-4 italic line-clamp-3">
              "{testimonial.text?.en || 'No text'}"
            </p>
            
            <div className="mb-4">
              <p className="text-white font-medium">{testimonial.name}</p>
              <p className="text-sm text-[#6B7280]">{testimonial.company}</p>
            </div>
            
            <div className="flex items-center gap-2 pt-4 border-t border-[#2a2d32]">
              <button
                onClick={() => openEditModal(testimonial)}
                className="flex-1 flex items-center justify-center gap-2 py-2 bg-[#2a2d32] hover:bg-[#3a3d42] rounded-lg transition-colors text-white text-sm"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => handleToggleActive(testimonial)}
                className="p-2 hover:bg-[#2a2d32] rounded-lg transition-colors"
              >
                {testimonial.active !== false ? (
                  <EyeOff className="w-4 h-4 text-[#9CA3AF]" />
                ) : (
                  <Eye className="w-4 h-4 text-[#9CA3AF]" />
                )}
              </button>
              <button
                onClick={() => handleDelete(testimonial.testimonial_id, testimonial.name)}
                className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#2a2d32]">
              <h2 className="text-xl font-bold text-white">
                {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-[#2a2d32] rounded-lg">
                <X className="w-5 h-5 text-[#9CA3AF]" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Client Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-[#141619] border-[#2a2d32] text-white"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Company</label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    className="bg-[#141619] border-[#2a2d32] text-white"
                    placeholder="Company Name"
                  />
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star className={`w-8 h-8 ${star <= formData.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#3a3d42]'}`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Testimonial Text */}
              <div>
                <h3 className="text-sm font-medium text-[#D4AF37] mb-3">Testimonial Text</h3>
                {['en', 'es', 'de'].map(lang => (
                  <div key={lang} className="mb-3">
                    <label className="block text-xs text-[#6B7280] mb-1">
                      {lang.toUpperCase()} {lang === 'en' && '*'}
                    </label>
                    <textarea
                      value={formData.text[lang]}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        text: { ...prev.text, [lang]: e.target.value }
                      }))}
                      className="w-full px-3 py-2 bg-[#141619] border border-[#2a2d32] rounded-lg text-white"
                      rows={3}
                      placeholder={lang === 'en' ? 'What the client said...' : ''}
                    />
                  </div>
                ))}
              </div>

              {/* Active */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                  className="w-4 h-4 rounded border-[#2a2d32] bg-[#141619] text-[#D4AF37]"
                />
                <span className="text-sm text-[#9CA3AF]">Visible on website</span>
              </label>
            </div>

            <div className="flex gap-3 p-6 border-t border-[#2a2d32]">
              <Button variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 border-[#2a2d32] text-white hover:bg-[#2a2d32]">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editingTestimonial ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManagement;
